import { createAppRoute } from "@/app/router/utils";
import { Uuid } from "@/domains/global/identifiers";
import { ImportKind } from "@/domains/integrations/imports";

/**
 * These are all the paths that are used in the app.
 *
 * Note - when adding new paths, you should also add new navigation helper functions
 * to our NavigationStore.
 *
 *   - store.navigate.goHome()
 *   - store.navigate.goToNote({ noteId: ... })
 *   - ...
 */
export const appRoutes = {
  root: createAppRoute(`/`),
  logIn: createAppRoute(`/login`),
  logInEmailPassword: createAppRoute(`/login/email-password`),
  logInOAuthToken: createAppRoute(`/login/oauth-token`),
  logOut: createAppRoute(`/logout`),
  googleOAuthRedirect: createAppRoute(`/google-oauth-redirect`),
  googleOAuthRedirectDesktop: createAppRoute(`/google-oauth-redirect-desktop`),
  oauthConsent: createAppRoute(`/oauth/consent`),
  invites: createAppRoute<{ inviteId: string }>(({ inviteId }) => `/invites/${inviteId}`),

  /**
   * Core Workflows
   */
  chatConversation: createAppRoute<{ sessionId: Uuid }>(({ sessionId }) => `/chat/${sessionId}`),
  search: createAppRoute(`/search`),
  trash: createAppRoute("/trash"),

  /**
   * Templates Endpoints
   */
  templatesList: createAppRoute("/templates"),
  templatesView: createAppRoute<{ templateId: Uuid }>(
    ({ templateId }) => `/templates/${templateId}`
  ),

  /**
   * V1 Migration Endpoints
   */
  v1MigrationGroups: createAppRoute(`/g`),
  v1MigrationNotes: createAppRoute(`/m`),
  v1MigrationTemplates: createAppRoute(`/sources/templates`),

  /**
   * Note Endpoints
   */
  notesList: createAppRoute(`/notes`),
  notesView: createAppRoute<{ noteId: Uuid }>(({ noteId }) => `/notes/${noteId}`),

  /**
   * Collection Endpoints
   */
  collections: createAppRoute("/collections"),
  collectionsView: createAppRoute<{ collectionId: Uuid }>(
    ({ collectionId }) => `/collections/${collectionId}`
  ),

  /**
   * Import Endpoints
   */
  importBatchNotes: createAppRoute<{ importBatchId: Uuid }>(
    ({ importBatchId }) => `/imports/${importBatchId}/notes`
  ),
  importBatchCollections: createAppRoute<{ importBatchId: Uuid }>(
    ({ importBatchId }) => `/imports/${importBatchId}/collections`
  ),

  /**
   * Integrations
   */
  integrations: createAppRoute("/integrations"),
  integrationsImports: createAppRoute("/integrations/imports"),
  integrationsImportsEvernote: createAppRoute(`/integrations/imports?kind=${ImportKind.EVERNOTE}`),
  integrationsImportsNotion: createAppRoute(`/integrations/imports?kind=${ImportKind.NOTION}`),
  integrationsImportsMarkdown: createAppRoute(`/integrations/imports?kind=${ImportKind.MARKDOWN}`),
  integrationsTwitter: createAppRoute("/integrations/twitter"),
  integrationsTexts: createAppRoute("/integrations/texts"),
  integrationsZapier: createAppRoute("/integrations/zapier"),
  integrationsApi: createAppRoute("/integrations/api"),
  integrationsEmail: createAppRoute("/integrations/email"),
  integrationsTextMessage: createAppRoute("/integrations/text-message"),
  integrationsChromeExtension: createAppRoute("/integrations/chrome-extension"),
  integrationsGoogleCalendar: createAppRoute("/integrations/calendar"),
  integrationsGoogleCalendarCallback: createAppRoute("/integrations/calendar/google/callback"),
  integrationsOutlookCalendarCallback: createAppRoute("/integrations/calendar/outlook/callback"),
  importFrom1dot0: createAppRoute("/settings/account-migration"),
  /**
   * Session Endpoints
   */
  sessionsList: createAppRoute("/sessions"),
  sessionsView: createAppRoute<{ sessionId: Uuid }>(({ sessionId }) => `/sessions/${sessionId}`),

  /**
   * Audio recordings
   */
  audioRecordings: createAppRoute("/audio-recordings"),
  audioRecording: createAppRoute<{ recordingId: Uuid }>(
    ({ recordingId }) => `/audio-recordings/${recordingId}`
  ),
  audioRecordingAdmin: createAppRoute<{ recordingId: Uuid }>(
    ({ recordingId }) => `/audio-recordings/${recordingId}/admin`
  ),
  audioRecordingCreate: createAppRoute("/audio-recordings/new"),
  audioRecordingActiveState: createAppRoute("/audio-recordings/active-state"),

  /**
   * Explore
   */
  explore: createAppRoute("/explore"),
  exploreTopicDetail: createAppRoute<{ topicId: Uuid }>(({ topicId }) => `/explore/${topicId}`),

  /**
   * Settings
   */
  settings: createAppRoute("/settings"),
  settingsEmailPreferences: createAppRoute("/settings/email-preferences"),
  settingsNotificationPreferences: createAppRoute("/settings/notification-preferences"),
  settingsVoiceMode: createAppRoute("/settings/voice-mode"),
  settingsSubscriptionManagement: createAppRoute("/settings/subscription-management"),
  settingsAccountManagement: createAppRoute("/settings/account-management"),
  settingsExports: createAppRoute("/settings/exports"),
  settingsAccountMigration: createAppRoute("/settings/account-migration"),
  settingsDebug: createAppRoute("/settings/debug"),
  settingsDebugAccount: createAppRoute("/settings/debug/account"),
  settingsDebugSandbox: createAppRoute("/settings/debug/sandbox"),

  /**
   * Admin Endpoints
   */
  admin: createAppRoute(`/admin`),

  /**
   * Debug Endpoints
   */
  debug: createAppRoute(`/debug`),
  debugDb: createAppRoute(`/debug/db`),

  /**
   * Deprecated endpoints
   */
  home: createAppRoute(`/home`), // DEPRECATED and redirects to /notes
  everything: createAppRoute(`/everything`), // DEPRECATED and redirects to /notes
} as const;
